import { z } from 'zod'

/**
 * Panel component types the Deep Research transcript is allowed to embed.
 *
 * These are a curated subset of the app-wide `ComponentType` enum
 * (see src/types/schemas/workspace.ts) — only the history-* panels that make
 * sense inside a research answer. All literals verified against the workspace
 * ComponentType enum.
 */
export const EMBEDDABLE_PANEL_TYPES = [
  'history-records',
  'history-stats',
  'history-intraday',
  'history-time-series',
  'history-scatter',
  'history-bar',
  'history-bar-category',
  'history-sankey',
  'history-calendar',
  // The interactive AI-summary chat panel was retired (folded into Clicky's
  // summarize_history tool), so there is no such embeddable type. Keep this list
  // in lockstep with the backend _EMBEDDABLE_PANEL_TYPES in deep_research_tools.py.
] as const

export type EmbeddablePanelType = (typeof EMBEDDABLE_PANEL_TYPES)[number]

/**
 * Loose filter validation — matches the house style used for panel filters
 * (see src/features/ticker-stats/schemas/stats-types.ts). Filters are either a
 * keyed object or a positional array; we do not model the filter tree here.
 */
const LooseFiltersSchema = z.union([z.record(z.unknown()), z.array(z.unknown())])

/** Symbolic → canonical ettalchemy operator tokens (mirror of the backend's
 * alias map in deep_research_tools.py). */
const OPERATOR_ALIASES: Record<string, string> = {
  '=': 'EQ',
  '==': 'EQ',
  '!=': 'NE',
  '<>': 'NE',
  '>': 'GT',
  '>=': 'GE',
  '<': 'LT',
  '<=': 'LE',
  BETWEEN: 'BT',
  NOT_IN: 'NIN',
}

/**
 * Normalize a filter tree to the house shape `/v2/history/*` accepts.
 *
 * Journaled panel_spec events replay verbatim forever, and early Deep Research
 * runs journaled the pre-fix `{op, children}` shape with symbolic operators
 * (`>=`). Convert those to `{AND|OR: [...]}` + canonical tokens so legacy
 * sessions still render; already-canonical trees pass through untouched, and
 * anything unrecognized is returned as-is (the panel request will surface it).
 */
export function normalizeHistoryFilterTree(raw: unknown): unknown {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return raw
  const node = raw as Record<string, unknown>

  const branchChildren = (kids: unknown): unknown =>
    Array.isArray(kids) ? kids.map(normalizeHistoryFilterTree) : kids

  // Legacy `{op: 'AND'|'OR', children: [...]}` branch.
  if (typeof node.op === 'string' && Array.isArray(node.children)) {
    const key = node.op.toUpperCase() === 'OR' ? 'OR' : 'AND'
    return { [key]: branchChildren(node.children) }
  }
  // House branch — normalize children in place.
  if (Array.isArray(node.AND)) return { AND: branchChildren(node.AND) }
  if (Array.isArray(node.OR)) return { OR: branchChildren(node.OR) }
  // Leaf — canonicalize the operator.
  if (typeof node.operator === 'string' && node.left != null) {
    const upper = node.operator.trim().toUpperCase()
    const operator = OPERATOR_ALIASES[node.operator.trim()] ?? OPERATOR_ALIASES[upper] ?? upper
    return { ...node, operator }
  }
  return raw
}

const NormalizedFiltersSchema = z.preprocess(normalizeHistoryFilterTree, LooseFiltersSchema)

/** An embeddable panel spec — a whitelisted component + its filters/state. */
export const PanelSpecSchema = z.object({
  title: z.string().optional(),
  component_type: z.enum(EMBEDDABLE_PANEL_TYPES),
  filters: NormalizedFiltersSchema,
  panel_state: z.record(z.unknown()).optional(),
  // 1-2 sentence "what this shows + why it matters". Optional here (legacy
  // journals predate it) though the backend tool now requires it.
  caption: z.string().optional(),
})

export type PanelSpec = z.infer<typeof PanelSpecSchema>

/**
 * What a panel visual fetched, captured while it rendered, so a saved report
 * renders it again without a query. Entries are keyed by request (see
 * workspace/inline-snapshot.ts); `data` is the response as received.
 */
export const VisualSnapshotSchema = z.object({
  taken_at: z.string(),
  entries: z.array(z.object({ key: z.string(), endpoint: z.string(), data: z.unknown() })),
})
export type VisualSnapshot = z.infer<typeof VisualSnapshotSchema>

/** GET /v1/deep-research/runs/:id/snapshots: what the run's panels fetched, keyed by visual id. */
export const RunSnapshotsResponseSchema = z
  .object({ snapshots: z.record(VisualSnapshotSchema).default({}) })
  .transform((r) => r.snapshots)
export type RunSnapshots = z.infer<typeof RunSnapshotsResponseSchema>

// ─── Chart / table specs ────────────────────────────────────────────────────

/** Chart specification sent by the AI via generate_chart tool. */
export const ChartSpecSchema = z.object({
  chart_type: z.enum(['bar', 'line', 'scatter', 'pie', 'area', 'histogram']),
  title: z.string(),
  data: z.array(z.record(z.unknown())),
  x_field: z.string(),
  y_fields: z.array(z.string()),
  // 1-2 sentence "what this shows + why it matters". Optional here (legacy
  // journals predate it) though the backend tool now requires it.
  caption: z.string().optional(),
})

export type ChartSpec = z.infer<typeof ChartSpecSchema>

/** Table specification sent by the AI via generate_table tool. */
export const TableSpecSchema = z.object({
  title: z.string(),
  columns: z.array(
    z.object({
      field: z.string(),
      headerName: z.string(),
    }),
  ),
  data: z.array(z.record(z.unknown())),
  caption: z.string().optional(),
})

export type TableSpec = z.infer<typeof TableSpecSchema>

// ─── Run / SSE event contract ───────────────────────────────────────────────

/**
 * Discriminated union over every run/journal/SSE event type. Every event
 * carries a monotonic `seq` (starts at 1) used for replay/reconnect dedupe.
 *
 * `data` events carry only a preview payload (ignored for the transcript) but
 * must still parse so the reducer can skip them cleanly. `chart_spec` /
 * `table_spec` specs are opaque — the same shape the DeepResearchChart /
 * table consumers accept.
 */
// ─── Report document ────────────────────────────────────────────────────────

/**
 * CONTRACT with edge-companion-api `deep-research/report.ts`, guarded by
 * report-contract.unit.test.ts against the companion's fixture. Sections mirror
 * the focus gallery's reading rail: what it shows, tendencies, bottom line.
 * `focus` names values that are really on the visual (the server validated them
 * against its result rows), so the renderer may trust them as data keys.
 */
/**
 * The closing section as blocks instead of a paragraph (server: report.ts
 * ReportSynthesis). `size` draws a driver's bar; null = the writer gave no number.
 */
export const ReportSynthesisSchema = z.object({
  takeaway: z.string().nullable(),
  drivers: z.array(
    z.object({ label: z.string(), effect: z.string(), size: z.number().nullable() }),
  ),
  outcomes: z.array(z.object({ label: z.string(), text: z.string() })),
  caveats: z.array(z.string()),
  next: z.string().nullable(),
})
export type ReportSynthesis = z.infer<typeof ReportSynthesisSchema>

export const ReportSectionSchema = z.object({
  id: z.string(),
  heading: z.string(),
  visual_id: z.string().nullable(),
  shows: z.string().nullable(),
  tendencies: z.array(z.object({ text: z.string(), n: z.number().nullable() })),
  bottom_line: z.string().nullable(),
  body: z.string(),
  focus: z.array(z.object({ key: z.string(), note: z.string() })),
  // Absent on every document journaled or saved before 2026-09-20.
  synthesis: ReportSynthesisSchema.nullish(),
})
export type ReportSection = z.infer<typeof ReportSectionSchema>

export const ReportDocumentSchema = z.object({
  version: z.number(),
  title: z.string(),
  bottom_line: z.string(),
  sections: z.array(ReportSectionSchema),
})
export type ReportDocument = z.infer<typeof ReportDocumentSchema>

export const RunEventSchema = z.discriminatedUnion('type', [
  z.object({
    seq: z.number(),
    type: z.literal('run_meta'),
    run_id: z.string(),
    session_id: z.string(),
    question: z.string(),
    status: z.string(),
    // The executor emits `(run_row or {}).get("started_at")` — null when the
    // run row is missing/pre-stamp. Nullish so one absent timestamp doesn't
    // reject (and thereby drop) the whole run_meta event.
    started_at: z.string().nullish(),
    // The run's own tool-round cap (server config). Optional: journals written
    // before 2026-09-17 do not carry it and fall back to MAX_RESEARCH_ROUNDS.
    max_rounds: z.number().nullish(),
    // The run's date range, resolved by the server before the run starts and held
    // to on every query. Journals written before 2026-09-18 do not carry it.
    window: z
      .object({
        start: z.string(),
        end: z.string(),
        label: z.string(),
        source: z.enum(['clarification', 'question', 'scope', 'default']),
      })
      .nullish(),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('thinking'),
    text: z.string(),
    round: z.number(),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('sql'),
    query: z.string(),
    round: z.number(),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('data'),
    preview: z.unknown(),
  }),
  // Lightweight, non-sensitive per-tool-call signal (name + round) sent to
  // every viewer. Drives the process-summary chip's counts without depending on
  // the SQL/thinking events that are redacted for non-admins.
  z.object({
    seq: z.number(),
    type: z.literal('tool'),
    name: z.string(),
    round: z.number(),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('chart_spec'),
    spec: z.record(z.unknown()),
    visual_id: z.string().optional(),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('table_spec'),
    spec: z.record(z.unknown()),
    visual_id: z.string().optional(),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('panel_spec'),
    title: z.string().optional(),
    component_type: z.enum(EMBEDDABLE_PANEL_TYPES),
    filters: NormalizedFiltersSchema,
    panel_state: z.record(z.unknown()).optional(),
    caption: z.string().optional(),
    visual_id: z.string().optional(),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('status'),
    round: z.number(),
    activity: z.string(),
  }),
  // The executor streamed a step's text as the answer and the model then
  // called a tool in that same step: drop the streamed text (it was
  // intermediate). Non-sensitive; sent to every viewer.
  z.object({
    seq: z.number(),
    type: z.literal('analysis_reset'),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('analysis'),
    text: z.string(),
  }),
  // The answer as a document (companion deep-research/report.ts): emitted once,
  // after the answer text and before done. Visual specs are not repeated in it;
  // sections point at the *_spec events by visual_id.
  ReportDocumentSchema.extend({
    seq: z.number(),
    type: z.literal('report'),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('error'),
    message: z.string(),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('cancelled'),
  }),
  z.object({
    seq: z.number(),
    type: z.literal('done'),
    status: z.string(),
  }),
])

export type RunEvent = z.infer<typeof RunEventSchema>

// ─── Start-run response ─────────────────────────────────────────────────────

/** Quota payload the server returns with a 429 when the plan limit is hit. */
export const RunQuotaSchema = z.object({
  used: z.number(),
  limit: z.number(),
  remaining: z.number(),
  allowed: z.boolean(),
})

export type RunQuota = z.infer<typeof RunQuotaSchema>

/**
 * Body of a successful `POST /deep-research/runs`. The router always sends
 * `session_id`/`status`, but only `run_id` is load-bearing for the client —
 * keep the rest optional so a lagging server can't brick run starts.
 */
export const RunStartResponseSchema = z
  .object({
    run_id: z.string(),
    session_id: z.string().optional(),
    status: z.string().optional(),
  })
  .passthrough()

export type RunStartResponse = z.infer<typeof RunStartResponseSchema>

// ─── Clarify (pre-run triage) ───────────────────────────────────────────────

/**
 * Pre-run clarification contract. `POST /v1/deep-research/clarify` triages a
 * question for ambiguity: `{clear: true}` when it's specific enough (or clarify
 * is unavailable — the endpoint fails open), otherwise 1-3 clarifying questions
 * with 2-4 selectable options each. `questions` is omitted on the clear path
 * (the router uses `response_model_exclude_none`), so it's optional here.
 */
export const ClarifyOptionSchema = z.object({
  label: z.string(),
  description: z.string(),
})

export type ClarifyOption = z.infer<typeof ClarifyOptionSchema>

export const ClarifyQuestionSchema = z.object({
  question: z.string(),
  header: z.string(),
  options: z.array(ClarifyOptionSchema),
})

export type ClarifyQuestion = z.infer<typeof ClarifyQuestionSchema>

/**
 * A question Deep Research will not run (scope gate, companion
 * deep-research/scope-gate.ts): the trader-facing message and where the
 * question belongs instead. Sent by clarify (new sessions) and as the body of
 * a 422 from `POST /runs` (follow-ups, which skip clarify).
 */
export const ScopeRedirectSchema = z.enum(['chat', 'backtest']).nullable()

export type ScopeRedirect = z.infer<typeof ScopeRedirectSchema>

export const ScopeRefusalSchema = z.object({
  message: z.string(),
  scope: z.string(),
  redirect: ScopeRedirectSchema,
  /** The stored check, so the trader can dispute it; absent before 1.56 is applied. */
  check_id: z.string().nullish(),
})

export type ScopeRefusal = z.infer<typeof ScopeRefusalSchema>

/**
 * The question states a criterion the chosen universe rules out, both sides in
 * trader words. The run locks the universe, so this is settled by buttons that
 * change the run's scope, not by a clarifying answer.
 */
export const ScopeConflictSchema = z.object({
  question_says: z.string(),
  scope_says: z.string(),
})

export type ScopeConflict = z.infer<typeof ScopeConflictSchema>

export const ClarifyResponseSchema = z.object({
  clear: z.boolean(),
  /**
   * Not a research question yet ("hello"). Its one question is for older pages
   * only: as a clarify card, Skip ran the message as a paid report.
   */
  needs_question: z.boolean().optional(),
  questions: z.array(ClarifyQuestionSchema).optional(),
  out_of_scope: ScopeRefusalSchema.optional(),
  scope_conflict: ScopeConflictSchema.optional(),
})

/**
 * A universe built for the question by `POST /v1/deep-research/recommend-universe`:
 * a flat AND of History filter leaves in the filter editor's own vocabulary,
 * with a trader-worded label and reason. Null when there is none.
 */
export const UniverseRecommendationSchema = z.object({
  label: z.string(),
  reason: z.string(),
  filters: z.object({
    AND: z.array(
      z.object({
        left: z.object({ column: z.string() }),
        operator: z.string(),
        right: z.object({ value: z.unknown() }),
      }),
    ),
  }),
})

export type UniverseRecommendation = z.infer<typeof UniverseRecommendationSchema>

/** Where step 2's "Recommended for your question" card is. */
export type RecommendationState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; rec: UniverseRecommendation }
  | { status: 'none' }

export const RecommendUniverseResponseSchema = z.object({
  recommendation: UniverseRecommendationSchema.nullable(),
})

/** Result of a clarify triage — the client treats any failure as `{clear: true}`. */
export type ClarifyResult = z.infer<typeof ClarifyResponseSchema>

// ─── Run status + summary ───────────────────────────────────────────────────

export const RunStatusSchema = z.enum(['pending', 'running', 'completed', 'failed', 'cancelled'])

export type RunStatus = z.infer<typeof RunStatusSchema>

/** Persisted summary of a run. Permissive — unknown keys pass through. */
export const RunSummarySchema = z
  .object({
    run_id: z.string(),
    session_id: z.string(),
    question: z.string(),
    status: RunStatusSchema,
    created_at: z.string(),
    /** True for an ancestor's run listed under a fork (read-only history). */
    inherited: z.boolean().optional(),
    /** Regenerate: the run this one re-asked with a different universe. */
    source_run_id: z.string().nullable().optional(),
    /**
     * A report (a Deep Research run) or an answer (one analyst stream, saved
     * when it ends). Absent before changeset 1.71: every run is a report.
     */
    kind: z.enum(['report', 'answer']).optional(),
    /** An answer's text (sent for answers only). */
    answer_text: z.string().nullable().optional(),
    /** An answer's tier, lookups and answer event id. */
    answer_meta: z.record(z.unknown()).nullable().optional(),
  })
  .passthrough()

export type RunSummary = z.infer<typeof RunSummarySchema>

/** Whether a run is an answer turn rather than a report. */
export function isAnswerRun(run: Pick<RunSummary, 'kind'>): boolean {
  return run.kind === 'answer'
}

// ─── Session list / detail ──────────────────────────────────────────────────

/** A row in the sessions list. Permissive — unknown keys pass through. */
export const DeepResearchSessionSchema = z
  .object({
    session_id: z.string(),
    title: z.string(),
    status: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    /** Lineage (a fork): the session this one branched from, and after which run. */
    parent_session_id: z.string().nullable().optional(),
    forked_after_run_id: z.string().nullable().optional(),
  })
  .passthrough()

export type DeepResearchSession = z.infer<typeof DeepResearchSessionSchema>

export const DeepResearchSessionListSchema = z.array(DeepResearchSessionSchema)

/**
 * Wire row of GET /v1/deep-research/sessions. The backend derives each
 * session's status from its newest run as `latest_status` (null when a session
 * has no runs) and allows a null title — adapt both to the flat
 * DeepResearchSession shape here so consumers never see the wire quirks.
 */
const DeepResearchSessionRowSchema = z
  .object({
    session_id: z.string(),
    title: z.string().nullable(),
    latest_status: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough()
  .transform(({ latest_status, ...rest }) => ({
    ...rest,
    title: rest.title ?? '',
    status: latest_status ?? 'pending',
  }))

/** Wire shape of GET /v1/deep-research/sessions — `{sessions: [...]}`. */
export const DeepResearchSessionListResponseSchema = z
  .object({ sessions: z.array(DeepResearchSessionRowSchema) })
  .transform((r) => r.sessions)

/** Session detail — the list row plus its runs (oldest-first, server-ordered). */
export const DeepResearchSessionDetailSchema = DeepResearchSessionSchema.extend({
  runs: z.array(RunSummarySchema).default([]),
  /** run_id -> report_id for the runs whose report the user has saved. */
  saved_reports: z.record(z.string()).default({}),
})

/**
 * Wire shape of GET /v1/deep-research/sessions/{id} — `{session, runs}`.
 * Runs come back oldest-first, so status derives from the LAST run.
 */
export const DeepResearchSessionDetailResponseSchema = z
  .object({
    session: z
      .object({
        session_id: z.string(),
        title: z.string().nullable(),
        created_at: z.string(),
        updated_at: z.string(),
        parent_session_id: z.string().nullable().optional(),
        forked_after_run_id: z.string().nullable().optional(),
      })
      .passthrough(),
    runs: z.array(RunSummarySchema).default([]),
    saved_reports: z.record(z.string()).default({}),
  })
  .transform(({ session, runs, saved_reports }) => ({
    ...session,
    title: session.title ?? '',
    status: runs.at(-1)?.status ?? 'pending',
    runs,
    saved_reports,
  }))

export type DeepResearchSessionDetail = z.infer<typeof DeepResearchSessionDetailSchema>

// ─── Transcript model ───────────────────────────────────────────────────────

/**
 * A single rendered block in the transcript. The reducer folds run events into
 * an ordered list of these; `status` events update progress rather than
 * producing a block.
 */
export type TranscriptBlock =
  | { kind: 'user'; text: string }
  | { kind: 'thinking'; text: string; round: number; collapsed?: boolean }
  | { kind: 'sql'; queries: string[]; round: number }
  | { kind: 'chart'; spec: Record<string, unknown>; visualId?: string }
  | { kind: 'table'; spec: Record<string, unknown>; visualId?: string }
  | { kind: 'panel'; spec: PanelSpec; visualId?: string }
  | { kind: 'analysis'; text: string }
  | { kind: 'error'; message: string }
  | { kind: 'cancelled' }

/** Progress state — the latest round/activity reported via `status` events. */
export interface TranscriptProgress {
  /** Tool-round cap announced by the run (`run_meta.max_rounds`), when known. */
  maxRounds?: number
  round?: number
  activity?: string
  startedAt?: string
}

/** Full folded transcript for one run. */
/** A run's date range and where it came from. */
export interface RunWindow {
  start: string
  end: string
  label: string
  source: 'clarification' | 'question' | 'scope' | 'default'
}

export interface Transcript {
  blocks: TranscriptBlock[]
  progress: TranscriptProgress
  status: RunStatus
  lastSeq: number
  runId?: string
  sessionId?: string
  /**
   * Every tool call the run made, in order, name-only (duplicates kept). Folded
   * from `tool` events. The process-summary chip derives its query/tool counts
   * from this so the counts survive even when SQL events are redacted.
   */
  toolCalls: string[]
  /** The run's answer as a document, once its `report` event has arrived. */
  report?: ReportDocument
  /** The run's resolved date range (run_meta), when the server sent one. */
  window?: RunWindow
}

/** Fresh, empty transcript in the `pending` state. */
export function emptyTranscript(): Transcript {
  return {
    blocks: [],
    progress: {},
    status: 'pending',
    lastSeq: 0,
    toolCalls: [],
  }
}

// ─── Saved reports ──────────────────────────────────────────────────────────

/** A row of GET /v1/deep-research/reports (the list never ships documents). */
export const SavedReportSummarySchema = z.object({
  report_id: z.string(),
  run_id: z.string().nullable(),
  session_id: z.string().nullable(),
  title: z.string(),
  question: z.string().nullable(),
  bottom_line: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  /** Set while the report is shared by link (detail only). */
  share_token: z.string().nullable().optional(),
})

// ─── Search / share ─────────────────────────────────────────────────────────

/** GET /v1/deep-research/search?q= */
export const SearchHitsSchema = z.object({
  sessions: z
    .array(
      z.object({ session_id: z.string(), title: z.string().nullable(), updated_at: z.string() }),
    )
    .default([]),
  runs: z
    .array(
      z.object({
        run_id: z.string(),
        session_id: z.string(),
        question: z.string(),
        created_at: z.string(),
        snippet: z.string().nullable().optional(),
      }),
    )
    .default([]),
  reports: z
    .array(
      z.object({
        report_id: z.string(),
        run_id: z.string().nullable(),
        session_id: z.string().nullable(),
        title: z.string(),
        created_at: z.string(),
        snippet: z.string().nullable().optional(),
      }),
    )
    .default([]),
})
export type SearchHits = z.infer<typeof SearchHitsSchema>

/** GET /v1/deep-research/shared/:token (no user). */
export const SharedReportResponseSchema = z
  .object({
    report: z.object({
      report_id: z.string(),
      title: z.string(),
      question: z.string().nullable(),
      bottom_line: z.string().nullable(),
      document: z.record(z.unknown()),
      created_at: z.string(),
    }),
  })
  .transform((r) => r.report)
export type SharedReport = z.infer<typeof SharedReportResponseSchema>
export type SavedReportSummary = z.infer<typeof SavedReportSummarySchema>

export const SavedReportListResponseSchema = z
  .object({ reports: z.array(SavedReportSummarySchema).default([]) })
  .transform((r) => r.reports)

/** GET /v1/deep-research/reports/{id}: the summary plus the self-contained document. */
export const SavedReportResponseSchema = z
  .object({ report: SavedReportSummarySchema.extend({ document: z.record(z.unknown()) }) })
  .transform((r) => r.report)
export type SavedReport = z.infer<typeof SavedReportResponseSchema>
